/**
 * Smart Drone — Dedicated DJI drone session manager & viewer.
 *
 * Features:
 * - Create multiple persistent drone sessions
 * - Full-screen HLS video viewer (projection-ready)
 * - Sessions persist until pilot stops
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/App";
import TopNav from "@/components/TopNav";
import {
  Camera, Monitor, Loader2, Maximize2, Minimize2, Copy, Plus,
  Wifi, WifiOff, Play, Trash2, ChevronLeft,
} from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const RELAY = "https://stream-relay-production.up.railway.app";
const getHeaders = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function SmartDrone() {
  const auth = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [droneModel, setDroneModel] = useState("M350");
  const [streamName, setStreamName] = useState("");

  const [viewingSession, setViewingSession] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [createdSession, setCreatedSession] = useState(null);

  useEffect(() => {
    fetchSessions();
    return () => { if (viewingSession) setViewingSession(null); };
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/drone/sessions`, {
        headers: getHeaders(), withCredentials: true,
      });
      setSessions(data.sessions || []);
    } catch { setSessions([]); }
    setLoading(false);
  };

  const createSession = async () => {
    setCreating(true);
    setCreatedSession(null);
    try {
      const name = streamName.trim() || ("dji-" + Date.now().toString(36));
      const { data } = await axios.post(`${API}/drone/session`,
        { drone_model: droneModel, stream_name: name },
        { headers: getHeaders(), withCredentials: true }
      );
      setCreatedSession(data);
      setStreamName("");
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create session");
    }
    setCreating(false);
  };

  const deleteSession = async (id) => {
    if (!window.confirm("End this session? The stream will stop.")) return;
    try {
      await axios.delete(`${API}/drone/session/${id}`, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Session ended");
      if (viewingSession?.id === id) setViewingSession(null);
      fetchSessions();
    } catch { toast.error("Failed to end session"); }
  };

  const toggleFullscreen = () => {
    const el = document.getElementById("drone-viewer-container");
    if (!isFullscreen) { if (el?.requestFullscreen) el.requestFullscreen(); }
    else { if (document.fullscreenElement) document.exitFullscreen(); }
  };

  // ── Session Viewer ──
  const SessionViewer = ({ session, onClose }) => {
    const videoElRef = useRef(null);
    const hlsRef = useRef(null);
    const pollRef = useRef(null);
    const [streamReady, setStreamReady] = useState(false);

    useEffect(() => {
      const video = videoElRef.current;
      if (!video) return;
      let closed = false;
      let hlsInstance = null;

      const tryHls = async () => {
        const name = session.stream_name;
        const urls = [
          RELAY + "/hls/live/" + name + "/stream.m3u8",
          RELAY + "/hls/live/" + name + ".m3u8",
          RELAY + "/hls/" + name + ".m3u8",
          session.hls_url,
        ];
        for (const url of urls) {
          if (!url) continue;
          try {
            const r = await fetch(url, { method: "HEAD" });
            if (r.ok && !closed) { setStreamReady(true); playHls(url, video); return; }
          } catch {}
        }
        pollFallback();
      };

      const playHls = (url, video) => {
        setStreamReady(true);
        if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.src = url; video.play().catch(() => {});
        } else {
          try {
            const Hls = window.Hls;
            if (Hls && Hls.isSupported()) {
              const h = new Hls();
              h.loadSource(url); h.attachMedia(video);
              h.on(h.constructor.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
              hlsRef.current = h; return;
            }
          } catch {}
          const s = document.createElement("script");
          s.src = "https://cdn.jsdelivr.net/npm/hls.js@latest";
          s.onload = () => {
            if (!closed && window.Hls?.isSupported()) {
              const h = new window.Hls();
              h.loadSource(url); h.attachMedia(video);
              h.on(h.constructor.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
              hlsRef.current = h;
            }
          };
          document.head.appendChild(s);
        }
      };

      const pollFallback = () => {
        const snap = RELAY + "/hls/" + encodeURIComponent(session.stream_name) + "/snapshot.webm";
        const trySnap = () => { fetch(snap).then(r => { if (r.ok && !closed && video) { video.src = snap; video.play().catch(() => {}); video.onended = () => { if (!closed) { video.src = snap; video.play().catch(() => {}); } }; setStreamReady(true); } }).catch(() => {}); };
        trySnap();
        pollRef.current = setInterval(trySnap, 8000);
        setTimeout(() => { if (!closed) setStreamReady(true); }, 3000);
      };

      tryHls();
      return () => { closed = true; if (pollRef.current) clearInterval(pollRef.current); if (hlsRef.current) try { hlsRef.current.destroy(); } catch {} };
    }, [session.stream_name]);

    return (
      <div id="drone-viewer-container" className="relative bg-black rounded-lg overflow-hidden" style={{ height: isFullscreen ? "100vh" : "70vh" }}>
        <video ref={videoElRef} autoPlay muted playsInline className="w-full h-full object-contain bg-black" />
        {!streamReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto mb-3" />
              <p className="text-slate-300 text-sm mb-1">Connecting to stream...</p>
              <p className="text-slate-500 text-xs">Waiting for drone feed via RTMP</p>
            </div>
          </div>
        )}
        {session.is_live && streamReady && (
          <div className="absolute top-3 left-3 z-10">
            <div className="flex items-center gap-1 bg-red-600 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              <span className="text-[10px] text-white font-semibold">LIVE</span>
            </div>
          </div>
        )}
        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
          <button onClick={onClose} className="text-white/70 hover:text-white bg-black/50 p-1.5 rounded"><ChevronLeft className="w-4 h-4" /></button>
        </div>
        {/* Fullscreen toggle */}
        <div className="absolute bottom-4 right-4 z-10">
          <button onClick={toggleFullscreen}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center shadow-lg">
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-7xl mx-auto px-3 py-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold">Smart Drone</h1>
            <p className="text-xs text-app-secondary">DJI drone session management & live viewing</p>
          </div>
        </div>

        {viewingSession ? (
          <SessionViewer session={viewingSession} onClose={() => setViewingSession(null)} />
        ) : (
          <div className="space-y-4">
            {/* Create Session */}
            <Card className="bg-app-card border-app-secondary/20 p-4">
              <h3 className="text-sm font-semibold mb-3">New Drone Session</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-app-secondary mb-1 block">Drone Model</Label>
                  <Select value={droneModel} onValueChange={setDroneModel}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                      <SelectItem value="M350">DJI Matrice 350 RTK</SelectItem>
                      <SelectItem value="Mavic3Pro">DJI Mavic 3 Pro</SelectItem>
                      <SelectItem value="Mavic3">DJI Mavic 3</SelectItem>
                      <SelectItem value="custom">Other Drone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-app-secondary mb-1 block">Stream Name (optional)</Label>
                  <Input value={streamName} onChange={(e) => setStreamName(e.target.value)}
                    placeholder="Auto-generated" className="bg-slate-700 border-slate-600 text-white text-sm" />
                </div>
                <div className="flex items-end">
                  <Button onClick={createSession} disabled={creating}
                    className="bg-sky-600 hover:bg-sky-500 text-white w-full">
                    {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4 mr-2" /> Create Session</>}
                  </Button>
                </div>
              </div>
            </Card>

            {/* RTMP Confirmation */}
            {createdSession && createdSession.rtmp_url && (
              <Card className="bg-emerald-900/30 border border-emerald-600/50 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center shrink-0">
                    <Wifi className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-emerald-400 mb-1">✅ Session Ready</h3>
                    <p className="text-xs text-slate-400 mb-3">Enter this RTMP URL in DJI Pilot 2 → Live Streaming → RTMP</p>
                    <Label className="text-[10px] text-slate-500 mb-1 block">RTMP URL</Label>
                    <div className="flex items-center gap-2 bg-slate-900/80 rounded px-3 py-2.5 mb-3">
                      <code className="flex-1 text-xs text-cyan-300 font-mono break-all select-all">{createdSession.rtmp_url}</code>
                      <button onClick={() => { navigator.clipboard.writeText(createdSession.rtmp_url); toast.success("Copied!"); }}
                        className="text-[10px] bg-cyan-700 hover:bg-cyan-600 text-white px-2.5 py-1.5 rounded shrink-0 font-medium">Copy</button>
                    </div>
                    {createdSession.full_url && (
                      <>
                        <Label className="text-[10px] text-slate-500 mb-1 block">Viewer Link</Label>
                        <div className="flex items-center gap-2 bg-slate-900/80 rounded px-3 py-2 mb-3">
                          <code className="flex-1 text-xs text-slate-300 font-mono break-all">{createdSession.full_url}</code>
                          <button onClick={() => { navigator.clipboard.writeText(createdSession.full_url); toast.success("Copied!"); }}
                            className="text-[10px] bg-zinc-700 hover:bg-zinc-600 text-white px-2.5 py-1.5 rounded shrink-0">Copy</button>
                        </div>
                      </>
                    )}
                    <div className="bg-slate-900/50 rounded p-3 text-xs text-slate-300 space-y-1">
                      <p className="text-emerald-400 font-semibold text-[10px] uppercase mb-1">📋 DJI Pilot 2 Setup</p>
                      <ol className="list-decimal ml-4 space-y-0.5 text-[11px]">
                        <li>Open DJI Pilot 2 → Settings → Live Streaming</li>
                        <li>Select RTMP as the platform</li>
                        <li>Paste the RTMP URL above</li>
                        <li>Tap Start — feed goes live within 15s</li>
                      </ol>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline"
                        onClick={() => setCreatedSession(null)}
                        className="text-xs border-slate-600 text-slate-300">Dismiss</Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Active Sessions */}
            <h3 className="text-sm font-semibold">Active Sessions</h3>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-app-secondary" /></div>
            ) : sessions.length === 0 ? (
              <Card className="bg-app-card border-app-secondary/20 p-8 text-center">
                <WifiOff className="w-10 h-10 text-app-secondary mx-auto mb-2" />
                <p className="text-sm text-app-secondary">No drone sessions</p>
                <p className="text-xs text-app-secondary mt-1">Create a session above, then push RTMP from DJI Pilot 2</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {sessions.map((s) => (
                  <Card key={s.id} className="bg-app-card border-app-secondary/20 overflow-hidden hover:border-sky-500/50 transition-colors">
                    <div className="h-32 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center relative">
                      <Camera className="w-10 h-10 text-slate-700" />
                      {s.is_live && (
                        <div className="absolute top-2 left-2 bg-red-600/80 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                          <span className="text-[10px] text-white font-medium">LIVE</span>
                        </div>
                      )}
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium truncate">{s.name || s.stream_name || "Drone"}</p>
                        {s.is_live ? (
                          <span className="text-[10px] text-green-400">● Live</span>
                        ) : (
                          <span className="text-[10px] text-slate-500">○ Offline</span>
                        )}
                      </div>
                      <p className="text-[10px] text-app-secondary truncate">{s.drone_model || "DJI"} · {s.stream_name}</p>
                      <div className="flex items-center gap-1.5 pt-1">
                        <Button size="sm" onClick={() => setViewingSession(s)}
                          className="flex-1 bg-sky-600 hover:bg-sky-500 text-white text-[10px] h-7">
                          <Play className="w-3 h-3 mr-1" /> View
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-app-secondary" onClick={() => {
                          navigator.clipboard.writeText(`rtmp://reseau.proxy.rlwy.net:57251/live/${s.stream_name}`);
                          toast.success("RTMP URL copied!");
                        }}>
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => deleteSession(s.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
